import React from 'react';
import { useGame } from '../context/GameContext';

interface CellProps {
  value: number;
  isObstacle: boolean;
  isSelected: boolean;
  isNextValid: boolean;
  onClick: () => void;
}

const Cell: React.FC<CellProps> = ({ value, isObstacle, isSelected, isNextValid, onClick }) => {
  return (
    <button
      className={`
        w-12 h-12 sm:w-16 sm:h-16 m-1 rounded-lg text-lg sm:text-xl font-bold transition-colors
        focus:outline-none focus:ring-2 focus:ring-blue-500
        ${isObstacle 
          ? 'bg-gray-800 text-transparent cursor-not-allowed' 
          : isSelected 
            ? 'bg-green-500 text-white cursor-default shadow-md' 
            : isNextValid 
              ? 'bg-blue-100 hover:bg-blue-200 text-blue-800 cursor-pointer shadow-md ring-2 ring-blue-300' 
              : 'bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-200'}
      `}
      onClick={onClick}
      disabled={isObstacle || isSelected}
      aria-label={isObstacle ? 'Obstacle' : `Number ${value}`}
      aria-disabled={isObstacle || isSelected}
    >
      {!isObstacle && value}
    </button>
  );
};

export const Grid: React.FC = () => {
  const { state, dispatch } = useGame();
  const { grid, obstacles, currentNumber, selectedCells, difficulty } = state;

  const isNextValid = (row: number, col: number): boolean => {
    console.log('Checking validity:', {
      clickedCell: { row, col, value: grid[row][col] },
      currentNumber,
      selectedCells,
      isObstacle: obstacles[row][col]
    });

    // Valid if it's the next number, not an obstacle and not already selected.
    // The game logic in `GameContext` already accepts selecting the correct next number,
    // so the grid should allow the click anywhere (no adjacency restriction here).
    const isValid = !obstacles[row][col] && !selectedCells.some(([r, c]) => r === row && c === col) && grid[row][col] === currentNumber;
    if (!isValid) {
      console.log('Not a valid next move');
    }
    return isValid;
  };

  const handleCellClick = (row: number, col: number) => {
    // Ignore clicks on obstacles or already selected cells
    if (obstacles[row][col] || selectedCells.some(([r, c]) => r === row && c === col)) {
      return;
    }

    const clickedValue = grid[row][col];
    console.log('Cell clicked:', { row, col, value: clickedValue, currentNumber });

    if (clickedValue === currentNumber) {
      // Allow selecting the next number anywhere on the grid. The reducer will handle completion.
      dispatch({ type: 'SELECT_CELL', payload: { row, col } });
      return;
    }

    // Only count as error if clicking a number cell (not empty or obstacle)
    if (clickedValue > 0) {
      console.log('Wrong number clicked - incrementing error');
      dispatch({ type: 'INCREMENT_ERROR' });
    }
  };

  // Debug log the current state
  console.log('Current game state:', {
    currentNumber,
    selectedCells,
    gridSize: difficulty.gridSize,
    showHints: difficulty.showHints
  });

  return (
    <div className="flex justify-center items-center">
      <div 
        className="inline-grid gap-1 p-2 sm:p-4"
        style={{
          gridTemplateColumns: `repeat(${difficulty.gridSize}, minmax(0, 1fr))`
        }}
        role="grid"
        aria-label="Number Sequencing Game Grid"
      >
        {grid.map((row, i) =>
          row.map((cell, j) => {
            const isSelected = selectedCells.some(([r, c]) => r === i && c === j);
            const isNextPossible = !isSelected && cell === currentNumber;
            const showHint = difficulty.showHints && isNextPossible;

            // For debugging
            if (cell === currentNumber) {
              console.log('Found current number:', {
                position: { row: i, col: j },
                isSelected,
                showHint
              });
            }

            return (
              <Cell
                key={`${i}-${j}`}
                value={cell}
                isObstacle={obstacles[i][j]}
                isSelected={isSelected}
                isNextValid={showHint}
                onClick={() => handleCellClick(i, j)}
              />
            );
          })
        )}
      </div>
    </div>
  );
};